import orderBy from "lodash/orderBy";
import {
  ChartCardRunData,
  useDashboardQuery,
  useLatestDashboardChartCardRunsQuery,
} from "src/graphql";
import { queryClient } from "src/lib/react-query";
import { STATIC_ARRAY } from "src/pages/analytics/state/constants";
import { useLoaderData } from "src/router";
import { DashboardData } from "./types";
import { getDashboardOptions } from "./utils";

const dashboardLoaderDataKey = Symbol("dashboardLoaderDataKey");
export type DashboardLoaderData = {
  [dashboardLoaderDataKey]: true;
  dashboard: DashboardData;
  chartCardData: Record<string, ChartCardRunData>;
  dashboardRunAt: Date | null;
};
export const isDashboardLoaderData = (x: unknown): x is DashboardLoaderData =>
  typeof x == "object" && !!x?.[dashboardLoaderDataKey];

export const dashboardLoader = async ({
  params,
}: {
  params: {
    dashboardId?: any;
    cohortFilter?: any;
    lookbackWindow?: any;
    useSampledModels?: any;
  };
}): Promise<DashboardLoaderData> => {
  const data = await queryClient.fetchQuery({
    queryKey: useDashboardQuery.getKey({ id: String(params.dashboardId) }),
    queryFn: useDashboardQuery.fetcher({ id: String(params.dashboardId) }),
  });

  const dashboard = data.dashboards_by_pk;
  if (!dashboard) {
    // biome-ignore lint/style/useThrowOnlyError: legacy suppression
    throw new Response("Not Found", { status: 404 });
  }

  const latestChartCardRunsData = await queryClient.fetchQuery({
    queryKey: useLatestDashboardChartCardRunsQuery.getKey({
      dashboardId: String(params.dashboardId),
      options: {
        cohortFilter:
          params.cohortFilter !== undefined
            ? params.cohortFilter
            : dashboard.cohort_filter,
        lookbackWindow:
          params.lookbackWindow !== undefined
            ? params.lookbackWindow
            : dashboard.lookback_window,
        useSampledModels:
          params.useSampledModels !== undefined
            ? params.useSampledModels
            : dashboard.use_sampled_models,
      },
    }),
    queryFn: useLatestDashboardChartCardRunsQuery.fetcher({
      dashboardId: String(params.dashboardId),
      options: getDashboardOptions({
        cohortFilter:
          params.cohortFilter !== undefined
            ? params.cohortFilter
            : dashboard.cohort_filter,
        lookbackWindow:
          params.lookbackWindow !== undefined
            ? params.lookbackWindow
            : dashboard.lookback_window,
        useSampledModels:
          params.useSampledModels !== undefined
            ? params.useSampledModels
            : Boolean(dashboard.use_sampled_models),
      }),
    }),
  });

  const chartCardData: Record<string, ChartCardRunData> = {};
  for (const chartCardRun of latestChartCardRunsData
    .getLatestDashboardChartCardRuns?.chartCardRuns ?? STATIC_ARRAY) {
    chartCardData[chartCardRun.chartCardId] = chartCardRun;
  }

  const rawDashboardRunAt =
    orderBy(
      latestChartCardRunsData.getLatestDashboardChartCardRuns?.chartCardRuns.map(
        ({ createdAt, pendingChartCardRun }) => ({
          runAt: pendingChartCardRun?.createdAt ?? createdAt,
        }),
      ),
      ({ runAt }) =>
        // Put null values at the end
        runAt ? new Date(runAt).getTime() : -Infinity,
      ["asc"],
    )[0]?.runAt ?? null;

  return {
    [dashboardLoaderDataKey]: true,
    dashboard,
    chartCardData,
    dashboardRunAt: rawDashboardRunAt ? new Date(rawDashboardRunAt) : null,
  };
};

export const useDashboardLoaderData = (): DashboardLoaderData => {
  const loaderData = useLoaderData();

  if (!isDashboardLoaderData(loaderData)) {
    throw new Error("invalid loader data for dashboard");
  }

  return loaderData;
};
